/**
 * QuietHoursSection Component
 *
 * Provides configuration for quiet hours - a time window during which
 * non-critical notifications are paused. Time selectors appear inline
 * with the checkbox when enabled.
 *
 * Design Compliance:
 * - Polaris spacing scale (gap="200", gap="400")
 * - Inline time selectors for compact layout
 * - Clear labeling and help text
 * - Uses parent timezone setting
 *
 * @example
 * ```tsx
 * <QuietHoursSection
 *   enabled={formData.quiet_hours_enabled}
 *   startTime={formData.quiet_hours_start}
 *   endTime={formData.quiet_hours_end}
 *   onEnabledChange={(enabled) => updateField('quiet_hours_enabled', enabled)}
 *   onStartTimeChange={(time) => updateField('quiet_hours_start', time)}
 *   onEndTimeChange={(time) => updateField('quiet_hours_end', time)}
 * />
 * ```
 */

import { BlockStack, InlineStack, Text, Checkbox, Select, Box } from '@shopify/polaris';
import { useCallback } from 'react';

// Generate time options in 30-minute increments
function generateTimeOptions(): Array<{ label: string; value: string }> {
  const options: Array<{ label: string; value: string }> = [];
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of [0, 30]) {
      const hourStr = hour.toString().padStart(2, '0');
      const minStr = minute.toString().padStart(2, '0');
      const value = `${hourStr}:${minStr}`;

      // Format for display (12-hour with AM/PM)
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${minStr} ${ampm}`;

      options.push({ label, value });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

export interface QuietHoursSectionProps {
  /** Whether quiet hours are enabled */
  enabled: boolean;
  /** Start time in HH:MM format */
  startTime: string;
  /** End time in HH:MM format */
  endTime: string;
  /** Handler for enabled change */
  onEnabledChange: (enabled: boolean) => void;
  /** Handler for start time change */
  onStartTimeChange: (time: string) => void;
  /** Handler for end time change */
  onEndTimeChange: (time: string) => void;
  /** @deprecated Timezone is now managed at parent level */
  timezone?: string;
  /** @deprecated Timezone is now managed at parent level */
  onTimezoneChange?: (timezone: string) => void;
}

export function QuietHoursSection({
  enabled,
  startTime,
  endTime,
  onEnabledChange,
  onStartTimeChange,
  onEndTimeChange,
}: QuietHoursSectionProps) {
  const handleEnabledChange = useCallback(
    (checked: boolean) => {
      onEnabledChange(checked);
    },
    [onEnabledChange]
  );

  return (
    <Box
      background="bg-surface"
      padding="400"
      borderColor="border"
      borderWidth="025"
      borderRadius="200"
    >
      <BlockStack gap="300">
        {/* Main row: Checkbox with inline time selectors when enabled */}
        <InlineStack align="space-between" blockAlign="start" wrap={false}>
          <Box>
            <Checkbox
              label={
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  Enable quiet hours
                </Text>
              }
              helpText="Pause non-critical notifications during specified hours"
              checked={enabled}
              onChange={handleEnabledChange}
            />
          </Box>

          {/* Time selectors - shown inline when enabled */}
          {enabled && (
            <InlineStack gap="200" blockAlign="center">
              <Box minWidth="120px">
                <Select
                  label="Start time"
                  labelHidden
                  options={TIME_OPTIONS}
                  value={startTime}
                  onChange={onStartTimeChange}
                />
              </Box>
              <Text as="span" variant="bodyMd" tone="subdued">
                to
              </Text>
              <Box minWidth="120px">
                <Select
                  label="End time"
                  labelHidden
                  options={TIME_OPTIONS}
                  value={endTime}
                  onChange={onEndTimeChange}
                />
              </Box>
            </InlineStack>
          )}
        </InlineStack>

        {/* Summary text - shown when enabled */}
        {enabled && (
          <Text as="p" variant="bodySm" tone="subdued">
            Critical alerts will still be delivered immediately.
          </Text>
        )}
      </BlockStack>
    </Box>
  );
}
